// frontend/src/pages/AdminDepartments.js
// Admin page for managing departments and their performance program assignment.
import React, { useEffect, useState } from 'react';
import {
  getDepartments, createDepartment, updateDepartment, deleteDepartment,
  getPrograms, createProgram, updateProgram,
} from '../services/api';
import { Card, Badge, Btn, Modal, Field, Input, Select, Textarea, Spinner, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';

export default function AdminDepartments() {
  const [departments, setDepartments] = useState([]);
  const [programs,    setPrograms]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  const [showDept, setShowDept] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const emptyDept = { name: '', program_id: '', sort_order: '' };
  const [deptForm, setDeptForm] = useState(emptyDept);

  const [showProg, setShowProg] = useState(false);
  const [editProg, setEditProg] = useState(null);
  const emptyProg = { name: '', description: '' };
  const [progForm, setProgForm] = useState(emptyProg);

  const load = () => Promise.all([
    getDepartments().catch(() => []),
    getPrograms().catch(() => []),
  ]).then(([d, p]) => {
    setDepartments(Array.isArray(d) ? d : []);
    setPrograms(Array.isArray(p) ? p : []);
  });

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const handleDeptProgramChange = async (dept, programId) => {
    if (String(dept.program_id) === String(programId)) return;
    const prog = programs.find(p => String(p.id) === String(programId));
    if (!window.confirm(`Switch "${dept.name}" to program "${prog?.name}"?\nAll ${dept.user_count || 0} user(s) in this department will use the new program's templates and cycles on next page load.`)) return;
    try {
      await updateDepartment(dept.id, { program_id: Number(programId) });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating department');
    }
  };

  const openDeptCreate = () => {
    setEditDept(null);
    setDeptForm({ ...emptyDept, program_id: programs[0]?.id ? String(programs[0].id) : '' });
    setShowDept(true);
  };
  const openDeptEdit = d => {
    setEditDept(d);
    setDeptForm({ name: d.name, program_id: String(d.program_id), sort_order: d.sort_order || '' });
    setShowDept(true);
  };

  const handleDeptSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editDept) await updateDepartment(editDept.id, deptForm);
      else await createDepartment(deptForm);
      await load(); setShowDept(false);
    } catch (err) { alert(err.response?.data?.error || 'Error saving department'); }
    finally { setSaving(false); }
  };

  const handleDeptDelete = async d => {
    if (!window.confirm(`Archive department "${d.name}"? Users assigned to it must be moved first.`)) return;
    try { await deleteDepartment(d.id); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Error archiving department'); }
  };

  const openProgCreate = () => { setEditProg(null); setProgForm(emptyProg); setShowProg(true); };
  const openProgEdit = p => { setEditProg(p); setProgForm({ name: p.name, description: p.description || '' }); setShowProg(true); };
  const handleProgSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editProg) await updateProgram(editProg.id, progForm);
      else await createProgram(progForm);
      await load(); setShowProg(false);
    } catch (err) { alert(err.response?.data?.error || 'Error saving program'); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="departments" helpTask="add-department" subtitle={'Assign each department to a performance management program. Users inherit their program from their department.'} />
      </div>

      {/* ── Departments ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <SectionTitle>Departments</SectionTitle>
        <Btn size="sm" onClick={openDeptCreate}>+ Add department</Btn>
      </div>
      <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Order','Name','Users','Program','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {departments.map(d => (
              <tr key={d.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', color: 'var(--text-3)' }}>{d.sort_order}</td>
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{d.name}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>{d.user_count}</td>
                <td style={{ padding: '12px 16px' }}>
                  <select
                    value={d.program_id}
                    onChange={e => handleDeptProgramChange(d, e.target.value)}
                    style={{ padding: '6px 10px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)', minWidth: 220 }}>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn variant="secondary" size="sm" onClick={() => openDeptEdit(d)}>Edit</Btn>
                    <Btn variant="ghost" size="sm" onClick={() => handleDeptDelete(d)} style={{ color: 'var(--red-500)' }}>Archive</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {departments.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>No departments yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* ── Performance programs ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <SectionTitle>Performance programs</SectionTitle>
        <Btn size="sm" onClick={openProgCreate}>+ Add program</Btn>
      </div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Name','Description','Departments','Templates','Cycles','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {programs.map(p => (
              <tr key={p.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{p.name}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-2)', fontSize: 12 }}>{p.description || '—'}</td>
                <td style={{ padding: '12px 16px' }}><Badge color="blue">{p.department_count}</Badge></td>
                <td style={{ padding: '12px 16px' }}><Badge color="green">{p.template_count}</Badge></td>
                <td style={{ padding: '12px 16px' }}><Badge color="amber">{p.cycle_count}</Badge></td>
                <td style={{ padding: '12px 16px' }}>
                  <Btn variant="secondary" size="sm" onClick={() => openProgEdit(p)}>Edit</Btn>
                </td>
              </tr>
            ))}
            {programs.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>No programs yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Department modal */}
      <Modal open={showDept} onClose={() => setShowDept(false)} title={editDept ? `Edit — ${editDept.name}` : 'New department'} width={440}>
        <form onSubmit={handleDeptSubmit}>
          <Field label="Name" required>
            <Input value={deptForm.name} onChange={e => setDeptForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Customer Success" />
          </Field>
          <Field label="Performance program" required>
            <Select value={deptForm.program_id} onChange={e => setDeptForm(f => ({ ...f, program_id: e.target.value }))}>
              <option value="">Select program…</option>
              {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Sort order">
            <Input type="number" min="0" value={deptForm.sort_order} onChange={e => setDeptForm(f => ({ ...f, sort_order: e.target.value }))} placeholder="Auto" />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn type="button" variant="secondary" onClick={() => setShowDept(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving || !deptForm.name || !deptForm.program_id}>{saving ? 'Saving…' : editDept ? 'Save' : 'Create'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Program modal */}
      <Modal open={showProg} onClose={() => setShowProg(false)} title={editProg ? `Edit — ${editProg.name}` : 'New performance program'} width={480}>
        <form onSubmit={handleProgSubmit}>
          <Field label="Name" required>
            <Input value={progForm.name} onChange={e => setProgForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sales Incentive Program" />
          </Field>
          <Field label="Description">
            <Textarea value={progForm.description} onChange={e => setProgForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="What this program covers — KPI approach, cycle cadence, bonus rules." />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn type="button" variant="secondary" onClick={() => setShowProg(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving || !progForm.name}>{saving ? 'Saving…' : editProg ? 'Save' : 'Create'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
